import { useTheme } from '@mui/material/styles';
import { useRef } from 'react';
import { BaseHighchart } from './BaseHighchart';

export const GaugeChart = ({
  data = [],
  value,
  min = 0,
  max = 100,
  thresholds,
  tierSplits,
  tierLabelOffset = 0,
  thresholdColors = ['#DF5353', '#DDDF0D', '#55BF3B'],
  legend,
  title = null,
  centerLabel,
  centerSubLabel,
  dataLabelFormatter,
  tierTooltipFormatter,
  onTierClick,
}) => {
  const theme = useTheme();
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;

  // Driven imperatively: routing band hovers through React state would rebuild the chart options on
  // every pointer move and re-animate the gauge.
  const tierTooltipRef = useRef(null);

  const positionTierTooltip = (event) => {
    const element = tierTooltipRef.current;
    if (!element) return;
    const gap = 14;
    const box = element.getBoundingClientRect();
    const left = Math.min(event.clientX + gap, window.innerWidth - box.width - gap);
    const top = Math.min(event.clientY + gap, window.innerHeight - box.height - gap);
    element.style.left = `${Math.max(gap, left)}px`;
    element.style.top = `${Math.max(gap, top)}px`;
  };

  const showTierTooltip = (lines, event) => {
    const element = tierTooltipRef.current;
    if (!element) return;
    element.replaceChildren(
      ...lines.map((line, index) => {
        const row = document.createElement('div');
        // textContent, never innerHTML - these lines carry chart data
        row.textContent = line;
        row.style.fontWeight = index === 0 ? '600' : '400';
        return row;
      }),
    );
    element.style.visibility = 'visible';
    positionTierTooltip(event);
  };

  const hideTierTooltip = () => {
    const element = tierTooltipRef.current;
    if (element) element.style.visibility = 'hidden';
  };

  const formatAmt = (value) => {
    if (value == null || isNaN(value)) return '$-';
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);

    const units = [
      { limit: 1e12, suffix: 'T' },
      { limit: 1e9, suffix: 'B' },
      { limit: 1e6, suffix: 'M' },
      { limit: 1e3, suffix: 'K' },
    ];

    let divisor = 1;
    let suffix = '';
    for (const u of units) {
      if (abs >= u.limit) {
        divisor = u.limit;
        suffix = u.suffix;
        break;
      }
    }

    const scaled = abs / divisor;
    let str = Number(scaled).toPrecision(3);
    str = String(+str); // drop trailing zeros / unnecessary decimals

    return `${sign}$${str}${suffix}`;
  };

  const gaugeValueRaw = value ?? data?.[0] ?? 80;
  const gaugeValue = Number.isFinite(Number(gaugeValueRaw)) ? Number(gaugeValueRaw) : 0;
  const gaugeMin = Number.isFinite(Number(min)) ? Number(min) : 0;
  const gaugeMax = Number.isFinite(Number(max)) && Number(max) > gaugeMin ? Number(max) : 100;

  const thresholdNums =
    Array.isArray(thresholds) ?
      thresholds
        .map((t) => Number(t))
        .filter((t) => Number.isFinite(t))
        .filter((t) => t > gaugeMin && t < gaugeMax)
        .sort((a, b) => a - b)
    : [];

  const boundaries = [gaugeMin, ...thresholdNums, gaugeMax];
  const colorForTier = (tierNumber) => {
    if (tierNumber < 0) {
      return thresholdColors[0] ?? '#DF5353';
    }
    return thresholdColors[Math.min(tierNumber, thresholdColors.length - 1)] ?? '#55BF3B';
  };

  const plotBands = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    const tierNumber = i + tierLabelOffset;
    const lowerBound = boundaries[i];
    const tooltipLines = tierTooltipFormatter?.(tierNumber, lowerBound);
    const bandEvents = {};
    if (tooltipLines?.length) {
      bandEvents.mouseover = (event) => showTierTooltip(tooltipLines, event);
      bandEvents.mousemove = positionTierTooltip;
      bandEvents.mouseout = hideTierTooltip;
    }
    if (onTierClick) {
      const showTooltip = bandEvents.mouseover;
      // Plot bands have no cursor option, so it is set on the band element as it is hovered.
      bandEvents.mouseover = (event) => {
        if (event.target?.style) event.target.style.cursor = 'pointer';
        showTooltip?.(event);
      };
      bandEvents.click = (event) => {
        hideTierTooltip();
        onTierClick(tierNumber, lowerBound, event);
      };
    }
    plotBands.push({
      from: lowerBound,
      to: boundaries[i + 1],
      color: colorForTier(tierNumber),
      thickness: 20,
      borderRadius: '50%',
      events: Object.keys(bandEvents).length ? bandEvents : undefined,
    });
  }

  const tierSplitNums =
    Array.isArray(tierSplits) ?
      tierSplits
        .map((t) => Number(t))
        .filter((t) => Number.isFinite(t))
        .filter((t) => t >= gaugeMin && t <= gaugeMax)
        .sort((a, b) => a - b)
        .filter((value, index, arr) => index === 0 || arr[index - 1] !== value)
    : [];

  const tierSplitLabelByValue = new Map();
  tierSplitNums.forEach((split, index) => {
    tierSplitLabelByValue.set(split, `Tier ${index + tierLabelOffset}<br/>${formatAmt(split)}`);
  });

  const legendOptions =
    legend == null ? { enabled: false }
    : legend === 'bottom' ? { enabled: true, align: 'center', verticalAlign: 'bottom' }
    : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'middle' };

  const options = {
    chart: {
      type: 'gauge',
      spacing: [8, 30, 8, 30],
      animation: { duration: 300 },
      events:
        centerLabel || centerSubLabel ?
          {
            render: function () {
              const chart = this;
              const series = chart.series && chart.series[0];
              if (!series) return;

              const [centerX, centerY] = series.center || [0, 0];
              const x = chart.plotLeft + centerX;
              const y = chart.plotTop + centerY;

              // Create/update the center title
              if (centerLabel) {
                if (!chart.customCenterTitle) {
                  chart.customCenterTitle = chart.renderer
                    .text(centerLabel, x, y)
                    .attr({
                      align: 'center',
                      'text-anchor': 'middle',
                      zIndex: 5,
                    })
                    .css({
                      fontSize: '13px',
                      fontWeight: 500,
                      color: chart.theme?.textColor || '#000',
                      pointerEvents: 'none',
                    })
                    .add();
                } else {
                  chart.customCenterTitle.attr({ text: String(centerLabel) }).css({ color: textPrimary });
                }
              }

              // Create/update the center subtitle
              if (centerSubLabel) {
                if (!chart.customCenterSubtitle) {
                  chart.customCenterSubtitle = chart.renderer
                    .text(String(centerSubLabel))
                    .attr({
                      align: 'center',
                      zIndex: 5,
                      'text-anchor': 'middle',
                      'dominant-baseline': 'middle',
                    })
                    .css({
                      fontSize: '12px',
                      fontWeight: 400,
                      color: textSecondary,
                      pointerEvents: 'none',
                    })
                    .add();
                } else {
                  chart.customCenterSubtitle.attr({ text: String(centerSubLabel) }).css({ color: textSecondary });
                }
              } else if (chart.customCenterSubtitle) {
                chart.customCenterSubtitle.destroy();
                chart.customCenterSubtitle = null;
              }

              // Position: title centered; subtitle just below with dynamic gap
              const gap = 0; // px between title and subtitle

              if (chart.customCenterTitle) {
                let reneredCustomCenterTitle = chart.customCenterTitle;
                if (reneredCustomCenterTitle.element) {
                  const box = reneredCustomCenterTitle.getBBox();
                  const verticalAdjust = box.height * 0.25; // ~25% tweak
                  reneredCustomCenterTitle.attr({ x, y: y - box.height / 2 + verticalAdjust });
                }
              }

              if (chart.customCenterSubtitle) {
                const titleBox = chart.customCenterTitle?.getBBox?.() || { height: 0 };
                const subtitleBox = chart.customCenterSubtitle.getBBox();
                const subtitleY = y + titleBox.height / 2 + gap + subtitleBox.height / 2;
                chart.customCenterSubtitle.attr({ x, y: subtitleY });
              }
            },
          }
        : undefined,
    },
    pane: {
      startAngle: -90,
      endAngle: 89.9,
      background: null,
      center: ['50%', '75%'],
      size: '90%',
    },
    title: { text: title ?? '', margin: 0 },
    subtitle: { text: '' },
    tooltip: { enabled: false },
    legend: legendOptions,
    yAxis: {
      min: gaugeMin,
      max: gaugeMax,
      title: { text: null },
      startOnTick: false,
      endOnTick: false,
      showFirstLabel: true,
      showLastLabel: true,
      tickPixelInterval: tierSplitNums.length ? undefined : 72,
      tickPositions: tierSplitNums.length ? tierSplitNums : undefined,
      tickPosition: 'inside',
      tickLength: 20,
      tickWidth: 2,
      minorTickInterval: null,
      labels: {
        enabled: true,
        distance: 35,
        y: 8,
        formatter: function () {
          if (!tierSplitNums.length) {
            return this.value;
          }
          const split = tierSplitNums.find((s) => Math.abs(s - this.value) < 0.000001);
          return split == null ? '' : (tierSplitLabelByValue.get(split) ?? '');
        },
        style: {
          fontSize: tierSplitNums.length ? '15px' : '18px',
        },
      },
      lineWidth: 0,
      plotBands,
    },
    plotOptions: {
      gauge: {
        dataLabels: {
          enabled: true,
          formatter:
            dataLabelFormatter ||
            function () {
              const amount = Number(this.y);
              const value =
                Number.isFinite(amount) ? amount.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '0';
              return 'Sales Amount: $' + value;
            },
          borderWidth: 0,
          y: 20,
        },
        dial: {
          radius: '80%',
          // backgroundColor: 'gray',
          baseWidth: 12,
          baseLength: '0%',
          rearLength: '0%',
        },
        pivot: {
          backgroundColor: 'gray',
          radius: 6,
        },
      },
    },
    series: [
      {
        id: 'gauge', // stable series id for smooth updates
        name: 'Sales Amount',
        data: [Math.min(Math.max(gaugeValue, gaugeMin), gaugeMax)],
        showInLegend: !!legendOptions.enabled,
      },
    ],
  };

  return (
    <>
      <BaseHighchart options={options} />
      <div
        ref={tierTooltipRef}
        style={{
          position: 'fixed',
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: theme.zIndex.tooltip,
          maxWidth: 340,
          padding: '6px 10px',
          borderRadius: 4,
          fontSize: 12,
          lineHeight: 1.5,
          color: textPrimary,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[3],
          overflowWrap: 'anywhere',
        }}
      />
    </>
  );
};
